import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, Link } from "@remix-run/react";
import { dehydrate, QueryClient, useQuery } from "@tanstack/react-query";
import { trpcServer } from "~/common/trcp";
import SpacesGrid from "~/components/workspaceListItems";
import { EnhancedPage } from "~/.server/api/routers/page";
import WorkspaceListLayout from "~/components/workspaceListLayout";
import { Button } from "~/components/ui/button";
import { PressBanner } from "~/components/pressBanner";
import { Copy } from "~/components/copy/copy";

const DEFAULT_OG_IMAGE = "https://images.compartirespacios.com/1220x780/workspace/1746602676393-pvrl184brf366nmaysje525w.jpg";
const SITE_URL = "https://www.compartirespacios.com";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.dehydratedState) return [];

  // Get the page data from the dehydrated state
  const page = data.dehydratedState.queries[0]?.state?.data as EnhancedPage;

  const title = page?.title || "Compartir Espacios";
  const description = page?.metaDescription || "Encuentra el espacio perfecto para tu negocio o proyecto. Oficinas, coworkings, talleres, locales y más.";

  return [
    { title },
    { name: "description", content: description },
    { property: "og:url", content: SITE_URL },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Compartir Espacios" },
    { property: "og:image", content: DEFAULT_OG_IMAGE },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    { tagName: "link", rel: "canonical", href: SITE_URL },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const queryClient = new QueryClient();
  const url = new URL(request.url);
  const lang = url.searchParams.get('lang') || 'es';
  // The translations editor loads this page in an iframe purely to preview
  // text copy — skip fetching/rendering the workspaces section so it doesn't
  // pay for a full listing query on every edit.
  const textOnly = url.searchParams.get('copyEdit') === '1';
  await queryClient.prefetchQuery({
    queryKey: ['page', 'home'],
    queryFn: async () =>
      await trpcServer(request).page.getPageBySlug.query({
        slug: 'home',
        language: lang as 'en' | 'es',
        textOnly
      })
  });

  return json({ 
    dehydratedState: dehydrate(queryClient)
  });
}


export default function Index() {
  const { data: page } = useQuery<EnhancedPage>({ 
    queryKey: ['page', 'home']
  });

  if (!page) return null;

  return (
    <div className="container mx-auto px-4">
      <div className="max-w-4xl mx-auto text-center py-12 px-4">
        <h1 className="text-3xl font-bold tracking-tight mb-6">
          <Copy id="home.hero.title" />
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          <Copy id="home.hero.description" />
        </p>
        <div className="flex justify-center gap-4">
          <Button asChild size="lg">
            <Link to="/cuenta/espacios"><Copy id="home.hero.ctaPublish" /></Link>
          </Button>
          <Button variant="outline" asChild size="lg">
            <Link to="/solicitar-espacio"><Copy id="home.hero.ctaRequest" /></Link>
          </Button>
        </div>
      </div>
      {page.content.map((section, index) => {
        switch (section.type) {
          case 'workspaces':
            const workspaces = Object.values(section.workspaces || {});
            return (
              <WorkspaceListLayout
                key={index}
                title={section.title}
                workspaces={workspaces}
                showBreadcrumbs={false}
              />
            );
          
          default:
            return null;
        }
      })}

      {/* Press Banner */}
      <PressBanner />
    </div>
  );
}
